import { APP_NAME } from "../graphql/store";
import type { IAlgoliaAppConfig } from "../types/api/algolia";

export const CONFIGS: Record<string, IAlgoliaAppConfig> = {
  tune: {
    meta: {
      hidden: { namespace: "custom", key: "hidden" },
      accessibleMemberships: {
        namespace: "custom",
        key: "accessible_memberships",
      },
      dropDate: { namespace: "operation", key: "drop_date" },
      categorySub: { namespace: "category", key: "sub" },
      color: { namespace: "filter", key: "color" },
      sale: { namespace: "operation", key: "sale" },
      releaseDate: { namespace: "operation", key: "release_date" },
    },
    facet: {
      vendor: "vendor",
      productType: "product_type",
      color: "meta.filter.color",
      size: "options.size",
      categorySub: "meta.category.sub",
      sale: "meta.operation.sale",
      tags: "tags",
      collections: "collections",
    },
    visibleProductsFilter: ['NOT tags:"Gift"', 'NOT vendor:"GWP"'].join(" AND "),
    imageSize: { width: 2000, height: 2500 },
  },

  /**
   * thisisneverthat (KR / INTL / JP 공통 스키마)
   *
   * - hidden 은 operation 네임스페이스를 쓴다 (tune 은 custom).
   * - 색상이 메타필드가 아니라 상품 옵션(options.color)이다.
   * - 색인에 들어오는 메타필드는 현재 operation.hidden 뿐이라, 나머지는 값이 없으면
   *   어댑터가 undefined 로 두고 화면이 기존 폴백 경로를 탄다.
   */
  thisisneverthat: {
    meta: {
      hidden: { namespace: "operation", key: "hidden" },
      accessibleMemberships: {
        namespace: "custom",
        key: "accessible_memberships",
      },
      dropDate: { namespace: "operation", key: "drop_date" },
      categorySub: { namespace: "category", key: "sub" },
      color: { namespace: "filter", key: "color" },
      sale: { namespace: "operation", key: "sale" },
      releaseDate: { namespace: "operation", key: "release_date" },
    },
    facet: {
      vendor: "vendor",
      productType: "product_type",
      color: "options.color",
      size: "options.size",
      categorySub: "meta.category.sub",
      sale: "meta.operation.sale",
      tags: "tags",
      collections: "collections",
    },
    visibleProductsFilter: ['NOT tags:"Gift"', 'NOT vendor:"GWP"'].join(" AND "),
    imageSize: { width: 2000, height: 2500 },
  },

  khakis: {
    meta: {
      hidden: { namespace: "operation", key: "hidden" },
      accessibleMemberships: {
        namespace: "custom",
        key: "accessible_memberships",
      },
      dropDate: { namespace: "operation", key: "drop_date" },
      categorySub: { namespace: "operation", key: "sub_category" },
      color: { namespace: "filter", key: "color" },
      sale: { namespace: "operation", key: "sale" },
      releaseDate: { namespace: "operation", key: "release_date" },
    },
    facet: {
      vendor: "vendor",
      productType: "product_type",
      color: "options.color",
      size: "options.size",
      category: "meta.operation.category",
      categorySub: "meta.operation.sub_category",
      sale: "meta.operation.sale",
      tags: "tags",
      collections: "collections",
    },
    // 기존 검색 화면과 동일하게 GWP 벤더만 제외한다.
    visibleProductsFilter: ['NOT tags:"Gift"', 'NOT vendor:"GWP"'].join(" AND "),
    imageSize: { width: 2000, height: 2500 },
  },
};

/** 현재 앱의 Algolia 설정. 스토어를 추가할 땐 위 CONFIGS 에 항목만 넣으면 된다. */
export const ALGOLIA_CONFIG: IAlgoliaAppConfig = CONFIGS[APP_NAME] ?? CONFIGS.tune;

/**
 * 이 고객에게 보여 줄 상품인지. hidden 상품과, 고객 태그가 맞지 않는 멤버십 전용 상품을 뺀다.
 * Algolia 결과(hitToProductNode)와 Shopify 예측검색 상품에 같이 쓴다.
 */
export function isVisibleToCustomer(
  node: { hidden?: { value?: string }; accessible_memberships?: { value?: string } },
  customerTags?: string[],
): boolean {
  if (node.hidden?.value !== "false") return false;
  if (!node.accessible_memberships?.value) return true;

  const accessibleMemberships = JSON.parse(node.accessible_memberships.value) as string[];
  return customerTags?.some((tag) => accessibleMemberships.includes(tag)) ?? false;
}
